/**
 * Login Page Component
 * Authentication page for user login
 */
import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, LogIn, Eye, EyeOff } from 'lucide-react'
import apiClient from '@/services/api/client'
import { startAuthSession } from '@/lib/authSession'
import { isManagerUser } from '@/lib/managerAccess'
import { isAddAccountSearch, pathAfterLogin, takeRedirectAfterLogin } from '@/lib/afterLogin'
import { useRegistrationStatus } from '@/hooks/useRegistrationStatus'
import { useAccountsSnapshot } from '@/hooks/useAccounts'
import type { LoginLocationState } from '@/components/auth/RegistrationGate'

export function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  // KAN-684: сообщение от редиректа (например, с закрытой /register)
  const notice = (location.state as LoginLocationState | null)?.message
  // KAN-925: /login?add=1 из меню "Добавить аккаунт": вошедшие остаются, есть путь назад
  const { active } = useAccountsSnapshot()
  const addMode = isAddAccountSearch(location.search) && active !== null
  const { allowed: registrationAllowed, isLoading: registrationStatusLoading } = useRegistrationStatus()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.post('/api/v1/auth/login/', {
        email,
        password,
      })

      // KAN-856: кеш прошлого пользователя этого браузера (['currentUser'] и
      // данные) не переживает вход: иначе менеджер после владельца видел
      // кнопки владельца ("Назначить в работу"), пока кеш не устареет.
      queryClient.clear()
      // KAN-925: вход добавляет аккаунт к вошедшим и делает его активным во вкладке.
      // KAN-753: менеджер сессии этого пользователя забывается при входе.
      startAuthSession(response.data.access, response.data.refresh, response.data.user)

      // Redirect to dashboard; KAN-854: менеджер с телефона сразу в приложение
      navigate(pathAfterLogin(isManagerUser(response.data.user), takeRedirectAfterLogin()))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(
        error.response?.data?.detail ||
        'Ошибка входа. Проверьте ваши учётные данные.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">
            SMETA AI
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            {addMode ? 'Добавить аккаунт' : 'Вход в аккаунт'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Платформа автоматизации строительных смет
          </p>
          {addMode && active && (
            <p className="mt-2 text-center text-sm text-gray-600">
              <Link to="/" className="font-medium text-blue-600 hover:text-blue-500" data-testid="login-add-cancel">
                Вернуться к аккаунту {active.name || active.email}
              </Link>
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {notice && !error && (
            <Alert>
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email адрес</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.ru"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">Пароль</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="text-right">
            <Link to="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-500">
              Забыли пароль?
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Вход...
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Войти
              </>
            )}
          </Button>
        </form>

        {/* KAN-684: ссылка на регистрацию только если она открыта */}
        {!registrationStatusLoading && (
          registrationAllowed ? (
            <p className="text-center text-sm text-gray-600">
              Нет аккаунта?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                Регистрация
              </Link>
            </p>
          ) : (
            <p className="text-center text-sm text-gray-600">
              Доступ выдаёт администратор
            </p>
          )
        )}
      </div>
    </div>
  )
}
